/**
 * Native only: when app_config.min_native_version names a newer version than
 * the installed binary, show an update screen instead of the app. Fails open
 * (web, unknown version, missing or unreadable config all render children).
 */
import type { ReactNode } from 'react';
import { Capacitor } from '@capacitor/core';
import { useQuery } from '@tanstack/react-query';
import { MIN_NATIVE_VERSION_KEY, needsNativeUpdate, parseMinNativeVersion } from '@/config/minNativeVersion';
import { getAppVersion } from '@/utils/appVersion';
import { openExternalUrl } from '@/utils/openExternalUrl';
import { ANDROID_PLAY_STORE_URL, IOS_APP_STORE_URL } from '@/utils/nativePremiumHandoff';

async function fetchUpdateRequired(platform: 'ios' | 'android'): Promise<boolean> {
  const { supabase } = await import('@/integrations/supabase/client');
  const { data, error } = await supabase
    .from('app_config')
    .select('value')
    .eq('key', MIN_NATIVE_VERSION_KEY)
    .maybeSingle();
  if (error) return false;
  const minimum = parseMinNativeVersion(data?.value)[platform];
  if (!minimum) return false;
  return needsNativeUpdate(await getAppVersion(), minimum);
}

export function MinVersionGate({ children }: { children: ReactNode }) {
  const native = Capacitor.isNativePlatform();
  const platform = Capacitor.getPlatform() === 'android' ? 'android' : 'ios';
  const { data: updateRequired } = useQuery({
    queryKey: ['min-native-version', platform],
    queryFn: () => fetchUpdateRequired(platform),
    enabled: native,
    staleTime: 10 * 60 * 1000,
    retry: 0,
  });

  if (!native || !updateRequired) return <>{children}</>;

  const storeUrl = platform === 'android' ? ANDROID_PLAY_STORE_URL : IOS_APP_STORE_URL;
  return (
    <main
      data-testid="min-version-gate"
      className="flex min-h-[100dvh] flex-col items-center justify-center gap-6 bg-jp-bg-page px-6 text-center text-jp-fg-primary"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <h1 className="max-w-xs font-jp-display text-3xl font-bold leading-tight [text-wrap:balance]">Update Journal Party to keep going.</h1>
      <button
        type="button"
        onClick={() => void openExternalUrl(storeUrl)}
        className="w-full max-w-xs rounded-jp-full bg-jp-accent-primary px-6 py-4 font-jp-display text-lg font-bold text-jp-ink"
      >
        {platform === 'android' ? 'Update in Google Play' : 'Update in the App Store'}
      </button>
    </main>
  );
}

export default MinVersionGate;
